import React from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface PropertyFiltersState {
  searchTerm: string
  filterPurpose: string
  filterType: string
  filterBedrooms: string
  filterBathrooms: string
  filterPriceMin: string
  filterPriceMax: string
}

export const FILTERS_INITIAL_STATE: PropertyFiltersState = {
  searchTerm: '',
  filterPurpose: 'all',
  filterType: 'all',
  filterBedrooms: 'all',
  filterBathrooms: 'all',
  filterPriceMin: '',
  filterPriceMax: '',
}

export interface FilterColorScheme {
  primary: string
  primaryText: string
  primaryRing: string
  primaryBorder: string
  badge: string
  panelBg: string
  panelBorder: string
  labelColor: string
  btnInactive: string
  btnInactiveHover: string
  inputFull: string
}

export const FILTER_COLOR_SCHEMES: Record<string, FilterColorScheme> = {
  gold: {
    primary: 'bg-[#C9A227]', primaryText: 'text-white',
    primaryRing: 'focus:ring-[#C9A227]/50', primaryBorder: 'focus:border-[#C9A227]',
    badge: 'bg-white text-[#C9A227]', panelBg: 'bg-[#fafafa]', panelBorder: 'border-gray-100',
    labelColor: 'text-gray-500', btnInactive: 'bg-white border border-gray-200 text-gray-600',
    btnInactiveHover: 'hover:border-[#C9A227]',
    inputFull: 'bg-white border-gray-200 text-gray-700 placeholder-gray-400 focus:ring-[#C9A227]/50 focus:border-[#C9A227]',
  },
  amber: {
    primary: 'bg-amber-500', primaryText: 'text-white',
    primaryRing: 'focus:ring-amber-500/50', primaryBorder: 'focus:border-amber-500',
    badge: 'bg-white text-amber-600', panelBg: 'bg-amber-50', panelBorder: 'border-amber-100',
    labelColor: 'text-amber-700', btnInactive: 'bg-white border border-amber-200 text-amber-700',
    btnInactiveHover: 'hover:border-amber-400',
    inputFull: 'bg-white border-amber-200 text-amber-900 placeholder-amber-400 focus:ring-amber-500/50 focus:border-amber-500',
  },
  purple: {
    primary: 'bg-purple-600', primaryText: 'text-white',
    primaryRing: 'focus:ring-purple-500/50', primaryBorder: 'focus:border-purple-500',
    badge: 'bg-white text-purple-600', panelBg: 'bg-slate-800/50', panelBorder: 'border-white/10',
    labelColor: 'text-slate-300', btnInactive: 'bg-white/10 border border-white/20 text-slate-300',
    btnInactiveHover: 'hover:border-purple-400',
    inputFull: 'bg-white/10 border-white/20 text-white placeholder-white/50 focus:ring-purple-500/50 focus:border-purple-500',
  },
  red: {
    primary: 'bg-yellow-400', primaryText: 'text-red-900',
    primaryRing: 'focus:ring-yellow-400/50', primaryBorder: 'focus:border-yellow-400',
    badge: 'bg-red-900 text-yellow-300', panelBg: 'bg-red-800/40', panelBorder: 'border-yellow-400/20',
    labelColor: 'text-yellow-200', btnInactive: 'bg-red-700/40 border border-yellow-400/30 text-yellow-200',
    btnInactiveHover: 'hover:border-yellow-400',
    inputFull: 'bg-red-700/30 border-yellow-400/30 text-yellow-100 placeholder-yellow-200/50 focus:ring-yellow-400/50 focus:border-yellow-400',
  },
  green: {
    primary: 'bg-emerald-600', primaryText: 'text-white',
    primaryRing: 'focus:ring-emerald-500/50', primaryBorder: 'focus:border-emerald-500',
    badge: 'bg-white text-emerald-600', panelBg: 'bg-emerald-50', panelBorder: 'border-emerald-100',
    labelColor: 'text-emerald-800', btnInactive: 'bg-white border border-emerald-200 text-emerald-700',
    btnInactiveHover: 'hover:border-emerald-500',
    inputFull: 'bg-white border-emerald-200 text-emerald-900 placeholder-emerald-400 focus:ring-emerald-500/50 focus:border-emerald-500',
  },
  navy: {
    primary: 'bg-blue-900', primaryText: 'text-white',
    primaryRing: 'focus:ring-blue-800/50', primaryBorder: 'focus:border-blue-800',
    badge: 'bg-white text-blue-900', panelBg: 'bg-slate-50', panelBorder: 'border-slate-200',
    labelColor: 'text-slate-500', btnInactive: 'bg-white border border-slate-200 text-slate-600',
    btnInactiveHover: 'hover:border-blue-800',
    inputFull: 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:ring-blue-800/50 focus:border-blue-800',
  },
}

interface PropertyFiltersProps {
  filters: PropertyFiltersState
  onFiltersChange: (filters: PropertyFiltersState) => void
  propertyTypes: string[]
  totalProperties: number
  filteredCount: number
  colorSchemeKey?: keyof typeof FILTER_COLOR_SCHEMES
  showFilters: boolean
  onToggleFilters: () => void
}

const PropertyFilters: React.FC<PropertyFiltersProps> = ({
  filters,
  onFiltersChange,
  propertyTypes,
  totalProperties,
  filteredCount,
  colorSchemeKey = 'gold',
  showFilters,
  onToggleFilters,
}) => {
  const { t } = useTranslation('properties')
  const cs = FILTER_COLOR_SCHEMES[colorSchemeKey]

  const update = (key: keyof PropertyFiltersState, value: string) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const clearAll = () => onFiltersChange(FILTERS_INITIAL_STATE)

  const activeCount = [
    filters.searchTerm,
    filters.filterPurpose !== 'all' ? filters.filterPurpose : '',
    filters.filterType !== 'all' ? filters.filterType : '',
    filters.filterBedrooms !== 'all' ? filters.filterBedrooms : '',
    filters.filterBathrooms !== 'all' ? filters.filterBathrooms : '',
    filters.filterPriceMin,
    filters.filterPriceMax,
  ].filter(Boolean).length

  const btnActive = `${cs.primary} ${cs.primaryText}`
  const btnInactive = `${cs.btnInactive} ${cs.btnInactiveHover}`

  const filterBtn = (current: string, value: string, label: string, field: keyof PropertyFiltersState) => (
    <button
      key={value}
      onClick={() => update(field, value)}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
        current === value ? btnActive : btnInactive
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="w-full">
      {/* Resultado contador */}
      {activeCount > 0 && (
        <p className={`text-sm ${cs.labelColor} mt-2`}>
          {t('showingCount', { filtered: filteredCount, total: totalProperties })}
        </p>
      )}

      {/* Barra búsqueda + botón filtros */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mt-6 max-w-2xl mx-auto">
        <div className="relative flex-1 w-full">
          <Search size={18} className={`absolute left-3 top-1/2 -translate-y-1/2 ${cs.labelColor}`} />
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => update('searchTerm', e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 border rounded-full text-sm focus:outline-none focus:ring-2 ${cs.inputFull}`}
          />
          {filters.searchTerm && (
            <button
              onClick={() => update('searchTerm', '')}
              className={`absolute right-3 top-1/2 -translate-y-1/2 ${cs.labelColor} hover:opacity-80`}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <button
          onClick={onToggleFilters}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
            showFilters || activeCount > 0 ? btnActive + ' shadow-md' : btnInactive
          }`}
        >
          <SlidersHorizontal size={16} />
          {t('filters')}
          {activeCount > 0 && (
            <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${cs.badge}`}>
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Panel de filtros expandible */}
      {showFilters && (
        <div className={`mt-6 p-6 rounded-2xl border max-w-4xl mx-auto ${cs.panelBg} ${cs.panelBorder}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">

            {/* Propósito */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('purpose')}
              </label>
              <div className="flex flex-wrap gap-2">
                {filterBtn(filters.filterPurpose, 'all', t('all'), 'filterPurpose')}
                {filterBtn(filters.filterPurpose, 'venta', t('sale'), 'filterPurpose')}
                {filterBtn(filters.filterPurpose, 'renta', t('rent'), 'filterPurpose')}
                {filterBtn(filters.filterPurpose, 'inversion', t('investment'), 'filterPurpose')}
                {filterBtn(filters.filterPurpose, 'leasing', t('leasing'), 'filterPurpose')}
              </div>
            </div>

            {/* Tipo */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('type')}
              </label>
              <div className="flex flex-wrap gap-2">
                {filterBtn(filters.filterType, 'all', t('all'), 'filterType')}
                {propertyTypes.map(pt =>
                  filterBtn(filters.filterType, pt, pt, 'filterType')
                )}
              </div>
            </div>

            {/* Recámaras */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('bedrooms')}
              </label>
              <div className="flex flex-wrap gap-2">
                {['all','1','2','3','4'].map(n =>
                  filterBtn(filters.filterBedrooms, n, n === 'all' ? t('all') : n === '4' ? '4+' : n, 'filterBedrooms')
                )}
              </div>
            </div>

            {/* Baños */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('bathrooms')}
              </label>
              <div className="flex flex-wrap gap-2">
                {['all','1','2','3'].map(n =>
                  filterBtn(filters.filterBathrooms, n, n === 'all' ? t('all') : n === '3' ? '3+' : n, 'filterBathrooms')
                )}
              </div>
            </div>

            {/* Precio mínimo */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('priceMin')}
              </label>
              <input
                type="number"
                placeholder={t('priceMinPlaceholder')}
                value={filters.filterPriceMin}
                onChange={(e) => update('filterPriceMin', e.target.value)}
                className={`w-full px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 ${cs.inputFull}`}
              />
            </div>

            {/* Precio máximo */}
            <div>
              <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${cs.labelColor}`}>
                {t('priceMax')}
              </label>
              <input
                type="number"
                placeholder={t('priceMaxPlaceholder')}
                value={filters.filterPriceMax}
                onChange={(e) => update('filterPriceMax', e.target.value)}
                className={`w-full px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 ${cs.inputFull}`}
              />
            </div>
          </div>

          {/* Limpiar filtros */}
          {activeCount > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200/30 flex justify-center">
              <button
                onClick={clearAll}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-full transition-colors ${cs.labelColor} hover:opacity-80`}
              >
                <X size={14} />
                {t('clearFilters')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PropertyFilters

export const applyFilters = (properties: any[], filters: PropertyFiltersState): any[] => {
  return properties.filter(property => {
    const term = filters.searchTerm.toLowerCase()
    const matchesSearch = !term ||
      property.title?.toLowerCase().includes(term) ||
      property.zone?.toLowerCase().includes(term) ||
      property.city?.toLowerCase().includes(term) ||
      property.description?.toLowerCase().includes(term) ||
      property.amenities?.some((a: string) => a.toLowerCase().includes(term))

    const matchesPurpose = filters.filterPurpose === 'all' ||
      property.purpose?.toLowerCase() === filters.filterPurpose

    const matchesType = filters.filterType === 'all' ||
      property.property_type === filters.filterType

    const matchesBedrooms = filters.filterBedrooms === 'all' ||
      (property.bedrooms && property.bedrooms >= parseInt(filters.filterBedrooms))

    const matchesBathrooms = filters.filterBathrooms === 'all' ||
      (property.bathrooms && property.bathrooms >= parseInt(filters.filterBathrooms))

    const matchesPriceMin = !filters.filterPriceMin ||
      property.price >= parseFloat(filters.filterPriceMin)

    const matchesPriceMax = !filters.filterPriceMax ||
      property.price <= parseFloat(filters.filterPriceMax)

    return matchesSearch && matchesPurpose && matchesType &&
           matchesBedrooms && matchesBathrooms && matchesPriceMin && matchesPriceMax
  })
}
